import type { JSX } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import type { Entity } from '../api.js';
import { t } from '../i18n.js';
import {
  onDeleteEntity,
  onPickCollection,
  onRemoveFromAlbum,
  onSend,
  onSendLink,
  select,
} from '../actions.js';
import { browserCanSend, prepareThumb, sendableOf } from '../send.js';
import { linkableOf, linkChoices } from '../link.js';
import { albums, entities, view } from '../state.js';
import { Confirm, deleteQuestion } from './parts.js';
import {
  IconAddToCollection,
  IconInfo,
  IconLink,
  IconMore,
  IconRemoveFrom,
  IconSend,
  IconTrash,
} from './icons.js';

/**
 * **Le menu d'objet — un seul, monté à l'identique partout.**
 *
 * C'est la pièce qui répond au constat d'usage : « un patchwork d'objets avec des
 * incohérences de manipulation ». La cause n'était pas le modèle, elle était ici
 * — chaque écran définissait ses propres actions. « Ajouter à une collection »
 * n'existait que dans la barre de sélection de la photothèque ; « Supprimer »
 * n'existait que sur une tuile ; l'arborescence, elle, n'offrait ni l'un ni
 * l'autre. Le même objet, selon l'endroit où on le regardait, ne se manipulait
 * pas de la même façon.
 *
 * La règle est donc **négative** et vaut critère d'acceptation : aucun écran ne
 * définit d'actions d'objet. Il monte celui-ci.
 *
 * **Ce qui n'y figure pas dit la règle.** Il n'y a pas de « Partager » sur une
 * note ou une photo, parce que le socle n'expose rien d'autre qu'une collection —
 * il n'existe aucune route pour partager une entité seule. Un bouton qui
 * promettrait le contraire mentirait, puis échouerait. Ce qu'on propose à la
 * place, « Ajouter à une collection… », est le chemin réel vers le même but, et
 * l'absence du premier enseigne le modèle mieux qu'une explication.
 *
 * **Et « Envoyer » n'est pas ce « Partager »-là** *(N6, 2026-08-18)*. L'entrée
 * ajoutée alors ne partage rien **dans** Synkara : elle passe les octets à la
 * feuille du système, pour qu'une autre application les reçoive. Le paragraphe
 * ci-dessus tient donc mot pour mot, et c'est précisément pourquoi le verbe est
 * « Envoyer » — dans cet écran, « partage » désigne déjà le partage à un cercle,
 * et `vocabulaire.spec.ts` l'impose. Un même mot pour les deux gestes rendrait
 * les deux illisibles.
 *
 * ### Un menu, et non trois icônes *(correction 4.6 bis)*
 *
 * En 4.5 bis, les trois actions étaient devenues des **boutons-icônes nus** : la
 * densité était le bon objectif, la conclusion était fausse. Un pictogramme de
 * 18 px sans libellé n'enseigne rien — le retour d'usage a été « je ne vois pas
 * comment on ajoute ou enlève quelque chose d'une collection », et c'était exact :
 * le geste existait, il ne se **voyait** pas. Une icône est un raccourci pour qui
 * connaît déjà l'action ; elle ne peut pas être le seul endroit où on l'apprend.
 *
 * Un point d'entrée par objet, donc, et **des mots derrière** — la même forme que
 * la feuille de détail du mobile, ce qui supprime au passage une divergence entre
 * les deux clients. Le nom du fichier récupère la largeur que trois boutons lui
 * prenaient, ce qui était le vrai problème de 4.5 bis.
 */
export function ObjectMenu({
  entity,
  id,
  /** Défini quand on regarde l'intérieur d'une collection : « Retirer » s'y ajoute. */
  collection,
  opened = false,
}: {
  readonly entity: Entity;
  readonly id: string;
  readonly collection?: string;
  /**
   * **L'objet est ouvert** — visionneuse ou lecteur —, donc ses octets sont
   * chauds : `urlFor` rend l'URL présignée depuis son cache, et le navigateur a
   * souvent déjà les octets qu'il affiche. C'est ce qui décide de « Envoyer »
   * *(N6)*, et le prédicat est bien celui-là, pas le type de l'objet.
   *
   * Depuis une tuile de la grille, le même envoi demanderait une présignature
   * **puis** un téléchargement complet après le clic — deux sauts contre une
   * activation utilisateur qui dure moins que ça. On ne propose donc pas un
   * geste dont on sait qu'il ratera une fois sur deux : on l'offre là où il
   * marche, et « Télécharger » reste partout.
   */
  readonly opened?: boolean;
}): JSX.Element {
  const [at, setAt] = useState<{ top: number; left: number } | undefined>(undefined);
  /** Le geste destructeur en attente de confirmation, s'il y en a un. */
  const [asking, setAsking] = useState<'delete' | 'remove' | 'link' | undefined>(undefined);
  const host = useRef<HTMLSpanElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const open = at !== undefined;
  // **Décidé sur la fiche, sans lire un octet** : c'est ce qui permet à l'entrée
  // d'exister ou non au rendu, plutôt que d'échouer après le clic.
  const sendable = opened ? sendableOf(entity) : undefined;
  // **Le lien, et vers quels partages** *(L1)*. Aucun partage exposé à un
  // cercle : l'entrée n'apparaît pas — personne d'autre ne pourrait l'ouvrir.
  const linkable = opened && browserCanSend('media') ? linkableOf(entity) : undefined;
  const v = view.value;
  const choices = linkable
    ? linkChoices(
        entity,
        v.kind === 'collection' ? v.aid : undefined,
        albums.value,
        entity.albums ? undefined : entities.value.find((e) => e.Fid === entity.Fid),
      )
    : [];

  // Un menu qui ne se ferme qu'en rechoisissant reste ouvert au-dessus du contenu
  // qu'on essaie de lire. Le clic extérieur et la touche d'échappement sont les
  // deux sorties que tout le monde essaie d'abord.
  useEffect(() => {
    if (!open) return;
    // **La confirmation en attente part avec la couche.** La laisser derrière
    // ferait rouvrir le menu sur « Supprimer ? » — une question dont on aurait
    // oublié qu'on l'avait posée, avec son bouton rouge sous le doigt.
    const close = (): void => {
      setAsking(undefined);
      setAt(undefined);
    };
    const onDown = (ev: MouseEvent): void => {
      if (!host.current?.contains(ev.target as Node)) close();
    };
    const onKey = (ev: KeyboardEvent): void => {
      if (ev.key === 'Escape') close();
    };
    // **Un défilement ferme le menu.** Une couche fixe ne suit pas son bouton :
    // la laisser ouverte pendant que la liste bouge la détacherait de l'objet
    // qu'elle vise, et le clic suivant porterait sur autre chose.
    const onScroll = (): void => close();
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open]);

  /**
   * **Une couche fixe, positionnée depuis le bouton** — et non un bloc absolu dans
   * la tuile.
   *
   * La forme absolue paraissait plus simple et ne pouvait pas marcher : une tuile
   * de grille fait dix rems, le menu quatorze, et la colonne de contenu défile
   * (`overflow-y: auto`, qui rogne aussi en x). Le menu sortait donc de sa colonne
   * et se retrouvait **sous le rail**, visible mais non cliquable — le pire des
   * deux états, et un parcours l'a attrapé exactement comme un utilisateur l'aurait
   * vécu.
   *
   * Le calcul borne les **quatre** côtés, et le bas n'est pas un raffinement : une
   * couche fixe ouverte sous le pli ne peut plus être ramenée à l'écran — faire
   * défiler ne la déplace pas, puisqu'elle ne suit pas la page. Le menu se retourne
   * donc au-dessus du bouton quand il ne tient pas en dessous, ce que fait tout
   * menu correct et ce que j'avais omis.
   */
  const toggle = (): void => {
    if (open) {
      setAsking(undefined);
      return setAt(undefined);
    }
    const rect = button.current?.getBoundingClientRect();
    if (!rect) return;

    const width = 224;
    const margin = 8;
    // La hauteur est **dérivée du nombre d'entrées**, pas mesurée : mesurer
    // demanderait de monter le menu pour le déplacer ensuite, donc un saut visible.
    //
    // **Elle prend la plus haute des deux faces** *(2026-08-16)* : la couche
    // montre soit le menu, soit une confirmation, et cette dernière est plus
    // haute — une question sur deux lignes, sa nuance, et deux boutons. Ne
    // dimensionner que pour le menu ferait déborder la confirmation sous le pli
    // pour les objets du bas de la grille, là où une couche fixe ne se ramène
    // plus à l'écran.
    const rows =
      2 +
      (entity.Aid === undefined ? 1 : 0) +
      (sendable !== undefined ? 1 : 0) +
      (choices.length > 0 ? 1 : 0);
    // Le choix du partage est la troisième face : une ligne de question, puis
    // un nom par partage.
    const height = Math.max(8 + rows * 38, 168, choices.length > 1 ? 46 + choices.length * 38 : 0);

    const left = Math.min(
      Math.max(margin, rect.right - width),
      window.innerWidth - width - margin,
    );
    const below = rect.bottom + 4;
    const top =
      below + height > window.innerHeight - margin
        ? Math.max(margin, rect.top - height - 4)
        : below;

    setAt({ top, left });
    // **La miniature se cherche à l'ouverture** : ~20 Ko, et c'est ce qui fait
    // partir le premier clic sans attendre — voir `prepareThumb`.
    if (linkable && choices.length > 0) void prepareThumb(entity, linkable);
  };

  const choose = (run: () => void): void => {
    setAsking(undefined);
    setAt(undefined);
    run();
  };

  return (
    <span class="obj-actions" ref={host}>
      <button
        type="button"
        class="icon-btn"
        ref={button}
        data-testid={`menu-${id}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('object.actions', { name: entity.Name })}
        title={t('object.actions', { name: entity.Name })}
        onClick={toggle}
      >
        <IconMore size={18} />
      </button>

      {open ? (
        <div
          class="obj-menu"
          role="menu"
          data-testid={`menu-open-${id}`}
          style={{ top: `${at.top}px`, left: `${at.left}px` }}
        >
          {/* **La confirmation remplace le menu, dans la même couche** : ouvrir
              une boîte de dialogue par-dessus déplacerait le regard et la cible
              au moment le plus délicat du geste. La couche a été dimensionnée
              pour elle (voir `toggle`), donc elle ne se retourne pas non plus. */}
          {asking === 'link' ? (
            <div class="link-choice" role="group" data-testid={`link-choice-${id}`}>
              <span class="confirm-text">{t('send.linkWhich')}</span>
              {choices.map((c) => (
                <button
                  type="button"
                  role="menuitem"
                  key={c.aid}
                  data-testid={`link-to-${c.aid}`}
                  onClick={() => choose(() => void onSendLink(entity, c.aid))}
                >
                  <IconLink size={18} />
                  <span>{c.name}</span>
                </button>
              ))}
            </div>
          ) : asking ? (
            <Confirm
              testId={`confirm-${id}`}
              question={
                asking === 'remove'
                  ? t('collection.confirmRemove')
                  : deleteQuestion(entity.Name ?? '')
              }
              note={asking === 'remove' ? t('collection.removeIsNotDelete') : undefined}
              verb={asking === 'remove' ? t('collection.remove') : t('space.delete')}
              onConfirm={() =>
                choose(() =>
                  asking === 'remove'
                    ? void onRemoveFromAlbum(collection!, id)
                    : void onDeleteEntity(entity),
                )
              }
              onCancel={() => setAsking(undefined)}
            />
          ) : (
          <>
          <button
            type="button"
            role="menuitem"
            data-testid={`inspect-${id}`}
            onClick={() => choose(() => void select(id))}
          >
            <IconInfo size={18} />
            <span>{t('gallery.inspect')}</span>
          </button>

          {/* **Envoyer vers une autre application.** Trois conditions, et
              aucune n'est un réglage : l'objet est ouvert (ses octets sont
              chauds), il a quelque chose à envoyer — un dérivé `standard` prêt
              et sous le plafond, ou une note qui porte du texte —, et le
              navigateur sait le faire. Firefox bureau ne sait pas, et l'entrée
              n'y paraît pas : un contrôle désactivé annoncerait une fonction
              qui n'existe pas pour cet utilisateur. */}
          {opened && sendable !== undefined && browserCanSend(sendable.kind) ? (
            <button
              type="button"
              role="menuitem"
              data-testid={`send-${id}`}
              onClick={() => choose(() => void onSend(entity))}
            >
              <IconSend size={18} />
              <span>{t('send.action')}</span>
            </button>
          ) : null}

          {/* **Envoyer un lien** *(L1)* : la miniature et l'adresse de la photo
              dans un partage. Un seul partage possible part sans question ;
              plusieurs se choisissent dans la même couche, comme une
              confirmation — et le clic sur un nom est l'activation qui ouvre la
              feuille. */}
          {choices.length > 0 ? (
            <button
              type="button"
              role="menuitem"
              data-testid={`send-link-${id}`}
              onClick={() =>
                choices.length === 1
                  ? choose(() => void onSendLink(entity, choices[0]!.aid))
                  : setAsking('link')
              }
            >
              <IconLink size={18} />
              <span>{t('send.link')}</span>
            </button>
          ) : null}

          {/* Une collection ne se met pas dans une collection : le socle lit
              `FIL#<fid>`, et un `aid` n'y résout rien. L'action est donc absente
              plutôt que présente et refusée après le clic. */}
          {entity.Aid === undefined ? (
            <button
              type="button"
              role="menuitem"
              data-testid={`addto-${id}`}
              onClick={() => choose(() => onPickCollection([id]))}
            >
              <IconAddToCollection size={18} />
              <span>{t('collection.addTo')}</span>
            </button>
          ) : null}

          {collection ? (
            <button
              type="button"
              role="menuitem"
              data-testid={`remove-${id}`}
              onClick={() => setAsking('remove')}
            >
              <IconRemoveFrom size={18} />
              {/* **La phrase a quitté le menu** *(2026-08-16)*. Elle s'y lisait
                  sous le verbe depuis le 2026-08-15 — mieux qu'en tête de la
                  collection, mais toujours trop tôt : on parcourt un menu pour y
                  trouver un verbe, pas pour y lire une nuance. Elle est désormais
                  dans la confirmation, c'est-à-dire **à l'instant où l'on
                  décide**. */}
              <span>{t('collection.remove')}</span>
            </button>
          ) : (
            <button
              type="button"
              role="menuitem"
              class="danger"
              data-testid={`delete-${id}`}
              onClick={() => setAsking('delete')}
            >
              <IconTrash size={18} />
              <span>{t('space.delete')}</span>
            </button>
          )}
          </>
          )}
        </div>
      ) : null}
    </span>
  );
}
